/**
 * Dashboard.
 *
 * Uma loja que acabou de assinar tem zero venda e zero saldo, e é assim que
 * ela começa de verdade. Esta tela mostra isso: o que já é medível — catálogo,
 * grade, unidades, checklist — aparece com o número certo, e o que depende de
 * venda e de estoque aparece como estado vazio dizendo o que falta.
 *
 * Quem decide isso é o servidor, pelo campo `fonte` de cada bloco
 * (`api/internal/modulos/painel`). A tela não inventa dado nem esconde bloco:
 * no dia em que a venda existir, os mesmos componentes se preenchem sozinhos.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { Segmentado } from '../../componentes/Segmentado'
import { Icone, type NomeIcone } from '../../componentes/Icone'
import { api, ErroApi } from '../../plataforma/api'
import { formatar, formatarMoeda } from '../../plataforma/dinheiro'
import { useSessao } from '../../plataforma/contexto-sessao'
import {
  carregarPainel,
  formatarPontos,
  type Aviso,
  type Barra,
  type Fonte,
  type KPI,
  type Painel,
  type Periodo,
} from '../../plataforma/painel'
import { LayoutApp } from './LayoutApp'

const PERIODOS: { chave: Periodo; rotulo: string }[] = [
  { chave: '7d', rotulo: '7 dias' },
  { chave: '30d', rotulo: '30 dias' },
  { chave: '12m', rotulo: '12 meses' },
]

const VISUAL_KPI: Record<string, { icone: NomeIcone; fundo: string; cor: string }> = {
  vendas_hoje: { icone: 'payments', fundo: 'bg-marca-suave', cor: 'text-marca' },
  ticket_medio: { icone: 'receipt_long', fundo: 'bg-divisoria', cor: 'text-texto' },
  pecas_vendidas: { icone: 'inventory_2', fundo: 'bg-atencao-suave', cor: 'text-atencao-texto' },
  margem_bruta: { icone: 'percent', fundo: 'bg-ok-suave', cor: 'text-ok-texto' },
}

const COR_MIX = ['bg-marca', 'bg-marca-clara', 'bg-marca-barra', 'bg-inativo']

export function TelaDashboard() {
  const { situacao } = useSessao()
  const sessao = situacao.estado === 'autenticado' ? situacao.sessao : null

  const [periodo, setPeriodo] = useState<Periodo>('7d')
  const [painel, setPainel] = useState<Painel | null>(null)
  const [erro, setErro] = useState('')

  useEffect(() => {
    const controle = new AbortController()

    carregarPainel(periodo, controle.signal)
      .then((dados) => {
        setPainel(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o painel.')
      })

    return () => controle.abort()
  }, [periodo])

  const primeiroNome = sessao?.usuario.nome.split(' ')[0] ?? ''
  const titulo = painel ? `${painel.cabecalho.saudacao}, ${primeiroNome}` : `Olá, ${primeiroNome}`

  if (!painel) {
    return (
      <LayoutApp titulo={titulo} subtitulo={erro ? '' : 'Carregando…'}>
        {erro ? (
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erro}
          </div>
        ) : (
          <div className="flex w-full flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-[130px] animate-pulse rounded-sistema bg-superficie" />
              ))}
            </div>
            <div className="h-[320px] animate-pulse rounded-sistema bg-superficie" />
          </div>
        )}
      </LayoutApp>
    )
  }

  return (
    <LayoutApp titulo={titulo} subtitulo={painel.cabecalho.data}>
      <div className="flex w-full flex-col gap-4">
        {painel.avisos.map((aviso) => (
          <FaixaDeAviso key={aviso.tipo} aviso={aviso} />
        ))}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {painel.kpis.map((kpi) => (
            <CartaoKPI key={kpi.chave} kpi={kpi} />
          ))}
        </div>

        <div className="grid items-start gap-4 xl:grid-cols-[1.55fr_1fr]">
          <div className="flex min-w-0 flex-col gap-4">
            <CartaoFaturamento
              faturamento={painel.faturamento}
              periodo={periodo}
              aoTrocarPeriodo={setPeriodo}
            />
            <CartaoGrade grade={painel.grade_risco} />
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            <CartaoTop top={painel.top_produtos} />
            <CartaoMix mix={painel.mix} />
            <CartaoTarefas />
            <CartaoCatalogo catalogo={painel.catalogo} />
          </div>
        </div>
      </div>
    </LayoutApp>
  )
}

/* ------------------------------------------------------------------ peças */

function Cartao({
  titulo,
  subtitulo,
  extra,
  children,
}: {
  titulo: string
  subtitulo?: string
  extra?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-titulo text-base font-semibold text-tinta">{titulo}</h2>
          {subtitulo && <p className="mt-[3px] text-[12.5px] text-texto-suave">{subtitulo}</p>}
        </div>
        {extra}
      </div>
      {children}
    </section>
  )
}

/**
 * Estado vazio.
 *
 * Diz o que ainda não existe e por quê, em vez de desenhar um gráfico chapado
 * em zero. Zero desenhado como dado é indistinguível de falha de
 * carregamento — este bloco é a diferença entre "não vendeu nada" e "não
 * carregou".
 */
function Vazio({ icone, texto }: { icone: NomeIcone; texto: string }) {
  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-8 text-center">
      <Icone nome={icone} tamanho={26} className="text-inativo" />
      <p className="max-w-[320px] text-[13px] leading-[1.55] text-texto-suave">{texto}</p>
    </div>
  )
}

function FaixaDeAviso({ aviso }: { aviso: Aviso }) {
  const { situacao } = useSessao()
  const [enviado, setEnviado] = useState(false)

  const atencao = aviso.tom === 'atencao'

  const reenviar = async () => {
    if (situacao.estado !== 'autenticado') return
    try {
      await api.post('/email/reenvios', { email: situacao.sessao.usuario.email })
    } finally {
      // Sucesso ou falha, a tela responde igual. O servidor não conta se o
      // endereço existe, e a interface não pode desmentir isso com um erro
      // que só aparece para e-mail cadastrado.
      setEnviado(true)
    }
  }

  return (
    <div
      className={`flex gap-3 rounded-sistema border px-4 py-3.5 ${
        atencao ? 'border-atencao-suave bg-atencao-fundo' : 'border-marca-borda bg-marca-fundo-2'
      }`}
    >
      <Icone
        nome={atencao ? 'warning' : 'info'}
        tamanho={20}
        className={atencao ? 'text-atencao-texto' : 'text-marca'}
      />
      <div className="flex-1">
        <p className={`text-sm font-semibold ${atencao ? 'text-atencao-texto' : 'text-marca-forte'}`}>
          {aviso.titulo}
        </p>
        <p className={`mt-[3px] text-[13px] ${atencao ? 'text-atencao-texto' : 'text-marca-forte'}`}>
          {aviso.detalhe}
        </p>
      </div>

      {aviso.destino === 'reenviar_confirmacao' && (
        <button
          type="button"
          disabled={enviado}
          onClick={() => void reenviar()}
          className="h-[34px] shrink-0 self-center rounded-sistema border border-atencao-suave bg-superficie px-3.5 text-[13px] font-semibold whitespace-nowrap text-atencao-texto hover:bg-atencao-fundo disabled:opacity-60"
        >
          {enviado ? 'Enviado' : aviso.acao}
        </button>
      )}
    </div>
  )
}

function CartaoKPI({ kpi }: { kpi: KPI }) {
  const visual = VISUAL_KPI[kpi.chave] ?? {
    icone: 'payments' as NomeIcone,
    fundo: 'bg-divisoria',
    cor: 'text-texto',
  }
  const medido = kpi.fonte === 'real'

  return (
    <div className="rounded-sistema border border-borda bg-superficie px-5 py-[18px] shadow-[var(--shadow-cartao)]">
      <div className="mb-3.5 flex items-center justify-between">
        <span className="text-[13px] text-texto-suave">{kpi.rotulo}</span>
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-sistema ${visual.fundo} ${
            medido ? visual.cor : 'text-inativo'
          }`}
        >
          <Icone nome={visual.icone} tamanho={19} />
        </span>
      </div>

      <div
        className={`tabular mb-2 font-titulo text-[28px] font-bold ${
          medido ? 'text-tinta' : 'text-inativo'
        }`}
      >
        {valorDoKPI(kpi)}
      </div>

      {kpi.variacao ? (
        <div
          className={`flex items-center gap-[5px] text-[12.5px] font-semibold ${
            kpi.variacao.sentido === 'baixa' ? 'text-atencao-texto' : 'text-ok'
          }`}
        >
          <Icone
            nome={kpi.variacao.sentido === 'baixa' ? 'trending_down' : 'trending_up'}
            tamanho={16}
          />
          {kpi.variacao.texto}
          <span className="font-normal text-texto-fraco">vs. ontem</span>
        </div>
      ) : (
        <p className="text-[12px] leading-[1.45] text-texto-fraco">{kpi.espera}</p>
      )}
    </div>
  )
}

function valorDoKPI(kpi: KPI): string {
  switch (kpi.formato) {
    case 'dinheiro':
      return formatarMoeda(kpi.valor)
    case 'percentual':
      return formatarPontos(kpi.valor)
    default:
      return String(kpi.valor)
  }
}

function CartaoFaturamento({
  faturamento,
  periodo,
  aoTrocarPeriodo,
}: {
  faturamento: Painel['faturamento']
  periodo: Periodo
  aoTrocarPeriodo: (p: Periodo) => void
}) {
  const seletor = (
    <Segmentado
      opcoes={PERIODOS}
      valor={periodo}
      aoTrocar={aoTrocarPeriodo}
      papel="aba"
      rotuloDoGrupo="Período do faturamento"
      compacto
    />
  )

  return (
    <Cartao titulo="Faturamento" subtitulo={faturamento.subtitulo} extra={seletor}>
      <div className="mb-5">
        <span
          className={`tabular font-titulo text-[32px] font-bold ${
            faturamento.fonte === 'real' ? 'text-tinta' : 'text-inativo'
          }`}
        >
          {formatarMoeda(faturamento.total_centavos)}
        </span>
      </div>

      <Grafico barras={faturamento.barras} fonte={faturamento.fonte} />

      {faturamento.espera && (
        <p className="mt-4 border-t border-divisoria pt-3.5 text-[12.5px] text-texto-fraco">
          {faturamento.espera}
        </p>
      )}
    </Cartao>
  )
}

/**
 * Gráfico de barras.
 *
 * Com todos os valores em zero — que é o estado de hoje — as barras viram um
 * traço na linha de base. Não é enfeite: é o eixo do período desenhado de
 * verdade, com os dias que a loja viveu. Altura arbitrária "para ficar
 * bonito" seria dado inventado.
 */
function Grafico({ barras, fonte }: { barras: Barra[]; fonte: Fonte }) {
  const maior = barras.reduce((maximo, barra) => Math.max(maximo, barra.valor_centavos), 0)

  return (
    <div className="flex h-[180px] items-end gap-3.5">
      {barras.map((barra, i) => {
        const altura = maior === 0 ? 3 : Math.round(6 + (barra.valor_centavos / maior) * 130)
        const destaque = maior > 0 && barra.valor_centavos === maior

        return (
          <div
            key={`${barra.rotulo}-${i}`}
            className="flex h-full flex-1 flex-col justify-end gap-2"
          >
            <span className="tabular text-center text-[11.5px] font-semibold text-texto-suave">
              {fonte === 'real' ? formatar(barra.valor_centavos) : ''}
            </span>
            <div
              style={{ height: altura }}
              className={`w-full rounded-t-[6px] ${destaque ? 'bg-marca' : 'bg-marca-suave'}`}
            />
            <span className="text-center text-[11.5px] text-texto-fraco">{barra.rotulo}</span>
          </div>
        )
      })}
    </div>
  )
}

function CartaoGrade({ grade }: { grade: Painel['grade_risco'] }) {
  if (grade.linhas.length === 0) {
    return (
      <Cartao titulo="Grade em risco" subtitulo="Tamanhos abaixo do mínimo">
        <Vazio icone="inventory_2" texto={grade.espera ?? 'Sem dados de estoque.'} />
        {grade.tamanhos.length > 0 && (
          <p className="border-t border-divisoria pt-3.5 text-center text-[12.5px] text-texto-fraco">
            A grade desta loja é {grade.tamanhos.join(' · ')}.
          </p>
        )}
      </Cartao>
    )
  }

  return (
    <Cartao titulo="Grade em risco" subtitulo="Tamanhos abaixo do mínimo">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-fundo">
            <th className="border-b border-borda px-4 py-2.5 text-left text-[12.5px] font-semibold text-texto-suave">
              Produto
            </th>
            {grade.tamanhos.map((tamanho) => (
              <th
                key={tamanho}
                className="border-b border-borda px-2.5 py-2.5 text-center text-[12.5px] font-semibold text-texto-suave"
              >
                {tamanho}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grade.linhas.map((linha) => (
            <tr key={`${linha.produto}-${linha.cor}`}>
              <td className="border-b border-divisoria px-4 py-2.5">
                <div className="text-[13.5px] font-semibold text-tinta">{linha.produto}</div>
                <div className="mt-[2px] text-xs text-texto-suave">{linha.cor}</div>
              </td>
              {linha.saldos.map((saldo, i) => (
                <td key={i} className="border-b border-divisoria px-2.5 py-3 text-center">
                  <SaldoDaGrade saldo={saldo} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Cartao>
  )
}

/** Badge de saldo: zero é vermelho, até 2 é amarelo, acima disso é verde. */
function SaldoDaGrade({ saldo }: { saldo: number }) {
  // -1 é "esta cor não tem este tamanho", que é diferente de "acabou". Um
  // traço diz isso; um zero vermelho mandaria comprar o que não existe.
  if (saldo < 0) {
    return <span className="text-[12.5px] text-inativo">—</span>
  }

  const cores =
    saldo === 0
      ? 'bg-erro-suave text-erro-texto'
      : saldo <= 2
        ? 'bg-atencao-suave text-atencao-texto'
        : 'bg-ok-suave text-ok-texto'

  return (
    <span
      className={`tabular inline-flex min-w-[28px] justify-center rounded-full px-2 py-[3px] text-[12.5px] font-semibold ${cores}`}
    >
      {saldo}
    </span>
  )
}

function CartaoTop({ top }: { top: Painel['top_produtos'] }) {
  return (
    <Cartao titulo="Top produtos" subtitulo="Por receita nos últimos 7 dias">
      {top.linhas.length === 0 ? (
        <Vazio icone="sell" texto={top.espera ?? 'Sem vendas no período.'} />
      ) : (
        top.linhas.map((linha) => (
          <div
            key={linha.produto}
            className="flex items-center gap-3 border-t border-divisoria py-2.5 first:border-t-0"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-sistema bg-divisoria text-texto-fraco">
              <Icone nome={linha.categoria === 'tenis' ? 'sprint' : 'apparel'} tamanho={18} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-semibold text-tinta">{linha.produto}</div>
              <div className="mt-[2px] text-xs text-texto-suave">{linha.pecas} peças</div>
            </div>
            <span className="tabular font-titulo text-sm font-bold text-tinta">
              {formatarMoeda(linha.receita_centavos)}
            </span>
          </div>
        ))
      )}
    </Cartao>
  )
}

function CartaoMix({ mix }: { mix: Painel['mix'] }) {
  const subtitulo = mix.base === 'catalogo' ? 'Participação no catálogo' : 'Participação na receita'

  return (
    <Cartao titulo="Mix por categoria" subtitulo={subtitulo}>
      {mix.fatias.length === 0 ? (
        <Vazio icone="sell" texto="Cadastre um produto para ver como o catálogo se divide." />
      ) : (
        mix.fatias.map((fatia, i) => (
          <div key={fatia.categoria} className="mb-3.5 last:mb-0">
            <div className="mb-[7px] flex justify-between text-[13px]">
              <span className="font-medium text-tinta">{fatia.rotulo}</span>
              <span className="tabular text-texto-suave">{formatarPontos(fatia.pontos_base)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-divisoria">
              <div
                className={`h-full rounded-full ${COR_MIX[i % COR_MIX.length]}`}
                style={{ width: formatarPontos(fatia.pontos_base) }}
              />
            </div>
          </div>
        ))
      )}
    </Cartao>
  )
}

type Tarefa = { chave: string; titulo: string; concluida: boolean }
type Checklist = { tarefas: Tarefa[]; concluidas: number; total: number }

/**
 * Tarefas do dia.
 *
 * O protótipo traz quatro tarefas de operação — conferir recebimento, imprimir
 * etiqueta, sangria, revisar NF-e — que dependem de módulos inexistentes. As
 * tarefas que existem de verdade hoje são as do primeiro acesso, e elas são,
 * de fato, o que a loja precisa fazer nos primeiros dias. Quando recebimento e
 * caixa existirem, esta lista soma as duas origens.
 */
function CartaoTarefas() {
  const { pode } = useSessao()
  const [checklist, setChecklist] = useState<Checklist | null>(null)
  const [salvando, setSalvando] = useState('')

  const podeMarcar = pode('onboarding.complete')

  useEffect(() => {
    const controle = new AbortController()
    api
      .get<Checklist>('/onboarding', controle.signal)
      .then(setChecklist)
      .catch(() => {
        // O cartão some. O painel não depende dele, e um erro vermelho aqui
        // assustaria por causa de um checklist.
      })
    return () => controle.abort()
  }, [])

  const alternar = useCallback(async (tarefa: Tarefa) => {
    setSalvando(tarefa.chave)
    try {
      setChecklist(
        await api.put<Checklist>(`/onboarding/tarefas/${tarefa.chave}`, {
          concluida: !tarefa.concluida,
        }),
      )
    } catch {
      // O servidor não mudou, e a lista na tela também não. Sem estado
      // otimista, não há o que desfazer.
    } finally {
      setSalvando('')
    }
  }, [])

  if (!checklist) return null

  return (
    <Cartao
      titulo="Tarefas do dia"
      subtitulo={`${checklist.concluidas} de ${checklist.total} concluídas`}
    >
      {checklist.tarefas.map((tarefa) => (
        <button
          key={tarefa.chave}
          type="button"
          disabled={!podeMarcar || salvando === tarefa.chave}
          aria-pressed={tarefa.concluida}
          onClick={() => void alternar(tarefa)}
          className="flex w-full items-center gap-[11px] border-t border-divisoria py-2.5 text-left first:border-t-0"
        >
          <span
            aria-hidden
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border-[1.5px] ${
              tarefa.concluida ? 'border-marca bg-marca text-white' : 'border-inativo bg-superficie'
            }`}
          >
            {tarefa.concluida && <Icone nome="check" tamanho={15} peso={600} />}
          </span>
          <span
            className={`text-[13.5px] ${
              tarefa.concluida ? 'text-texto-fraco line-through' : 'text-tinta'
            }`}
          >
            {tarefa.titulo}
          </span>
        </button>
      ))}
    </Cartao>
  )
}

/**
 * Catálogo da loja.
 *
 * No protótipo este cartão é "canais sincronizados", com e-commerce e NFC-e.
 * Nenhuma das duas integrações existe, e três linhas inventadas não valem o
 * pixel — no lugar entram três contagens verdadeiras.
 */
function CartaoCatalogo({ catalogo }: { catalogo: Painel['catalogo'] }) {
  const linhas = [
    { rotulo: 'Produtos ativos', valor: catalogo.produtos },
    { rotulo: 'SKUs na grade', valor: catalogo.skus },
    { rotulo: 'Unidades', valor: catalogo.unidades },
  ]

  return (
    <section className="rounded-sistema bg-tinta p-5">
      <div className="mb-3.5 flex items-center gap-2.5">
        <Icone nome="sync" tamanho={20} className="text-ok" />
        <h2 className="font-titulo text-[15px] font-semibold text-white">Catálogo da loja</h2>
      </div>
      {linhas.map((linha) => (
        <div
          key={linha.rotulo}
          className="flex justify-between border-t border-menu-realce py-[7px] text-[13px] text-texto-fraco"
        >
          <span>{linha.rotulo}</span>
          <span className="tabular font-semibold text-white">{linha.valor}</span>
        </div>
      ))}
    </section>
  )
}
